import { Button, Divider, Loader, PinInput, Text } from '@mantine/core'
import { useFullscreenDocument } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { IconDeviceTablet, IconFileText, IconMaximize, IconQrcode } from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  api,
  deviceName,
  FilesWork,
  type HandIn,
  isApiError,
  listenLive,
  pairingToken,
  QrViewfinder,
  SignaturePad,
  SignatureWork,
  screenApi,
  uploadSignature,
  useWakeLock,
} from '@tnn/app-kit'
import { formatDate, formatTime } from '@tnn/core'
import { type PublicSite, SCREEN_CODE_LENGTH, type ScreenPoint, type ScreenState, type Task } from '@tnn/domain'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClockScreen, clockKey, clockUnpaired } from './clock'
import { Frame, useNow } from './frame'
import classes from './screen.module.css'

const c = (key: string) => `screen.${key}`

/** How long the thanks stays before the screen goes back to waiting */
const THANKS_MS = 3000
const stateKey = ['screen', 'state'] as const
const unpairedError = (error: unknown) => isApiError(error) && String(error.code) === 'SCREEN_UNPAIRED'

/** Files and signatures from here go up as the screen's person's, and are handed in by the screen */
const handIn: HandIn = { uploadPath: '/screen/files', done: screenApi.done }

/** A key point with its figures bold and underlined, as the bill prints them */
function Point({ point }: { point: ScreenPoint }) {
  return point.map((part, i) =>
    part.figure ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one sentence, in order
      <strong key={i}>
        <u>{part.text}</u>
      </strong>
    ) : (
      // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one sentence, in order
      <span key={i}>{part.text}</span>
    ),
  )
}

/**
 * Pairing this device: scanning the QR its person shows (`PairScreenDialog` in app-kit) — or HR shows for a branch's
 * clock screen — with its camera, typing the code under it, or opening the QR's link (`/#<token>`) — with a phone's own
 * camera, or from its person's Thananan OS on this very device
 */
function Pairing({ onPaired }: { onPaired: () => void }) {
  const { t } = useTranslation()
  const [scanning, setScanning] = useState(false)
  const [code, setCode] = useState('')
  const [problem, setProblem] = useState<string>()
  const [busy, setBusy] = useState(false)
  const paired = useRef(onPaired)
  paired.current = onPaired

  const pair = async (input: { code?: string; token?: string }) => {
    setBusy(true)
    setProblem(undefined)
    try {
      await screenApi.pair({ ...input, name: deviceName(t) })
      paired.current()
    } catch (err) {
      setProblem(isApiError(err) ? err.message : t(c('failed')))
      setCode('')
    } finally {
      setBusy(false)
    }
  }
  const pairRef = useRef(pair)
  pairRef.current = pair

  // Opened from the QR's link: pair with its token once, and take it off the address
  useEffect(() => {
    const token = pairingToken(window.location.hash.slice(1))
    if (!token) return
    window.history.replaceState(null, '', window.location.pathname)
    pairRef.current({ token })
  }, [])

  return (
    <section className={classes.card}>
      <span className={classes.cardIcon} aria-hidden>
        <IconDeviceTablet size={30} stroke={1.75} />
      </span>
      <h1 className={classes.cardTitle}>{t(c('pairHere'))}</h1>
      <p className={classes.cardHint}>{t(c('pairHereHint'), { button: t(c('pair')), device: t(c('pairDevice')) })}</p>
      {scanning ? (
        <div className={classes.viewfinder}>
          <QrViewfinder
            onCode={(text) => {
              const token = pairingToken(text)
              if (!token || busy) return
              setScanning(false)
              pair({ token })
            }}
            onError={() => {
              setScanning(false)
              setProblem(t(c('noCamera')))
            }}
            onClose={() => setScanning(false)}
          />
        </div>
      ) : (
        <Button
          size="lg"
          radius="xl"
          fullWidth
          leftSection={<IconQrcode size={22} stroke={1.75} />}
          onClick={() => setScanning(true)}
          loading={busy}
        >
          {t(c('scan'))}
        </Button>
      )}
      <Divider label={t(c('orCode'))} labelPosition="center" w="100%" className={classes.or} />
      <PinInput
        length={SCREEN_CODE_LENGTH}
        type="number"
        size="lg"
        gap={6}
        oneTimeCode={false}
        value={code}
        onChange={setCode}
        onComplete={(typed) => pair({ code: typed })}
        disabled={busy}
        aria-label={t(c('code'))}
        classNames={{ root: classes.pins, pinInput: classes.pin, input: classes.digit }}
      />
      {problem && (
        <Text c="var(--tnn-error)" role="alert">
          {problem}
        </Text>
      )}
    </section>
  )
}

/** Someone at the counter signs: what they read, then what they agree to, a large pad, and one button */
function SignTo({ task, onDone }: { task: Task; onDone: () => void }) {
  const { t } = useTranslation()
  const [signature, setSignature] = useState<Blob | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string>()
  const ask = task.ask
  const confirm = async () => {
    if (!signature) return
    setBusy(true)
    setProblem(undefined)
    try {
      const fileId = await uploadSignature({ blob: signature, method: 'drawn' }, handIn.uploadPath)
      await screenApi.done(task.id, { fileIds: [fileId], signatureMethod: 'drawn' })
      onDone()
    } catch (err) {
      setProblem(isApiError(err) ? err.message : t('signature.failed'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <div className={classes.reading}>
        <h1 className={classes.title}>{ask?.title ?? task.label}</h1>
        {ask?.intro?.map((line) => (
          <p key={line} className={classes.intro}>
            {line}
          </p>
        ))}
        {ask?.points?.length ? (
          <ol className={classes.points}>
            {ask.points.map((point) => (
              <li key={point.map((part) => part.text).join('')}>
                <span>
                  <Point point={point} />
                </span>
              </li>
            ))}
          </ol>
        ) : null}
      </div>
      <div className={classes.signing}>
        {ask?.statement && <p className={classes.statement}>{ask.statement}</p>}
        <SignaturePad onChange={setSignature} large />
        {problem && (
          <Text c="var(--tnn-error)" role="alert">
            {problem}
          </Text>
        )}
        <Button size="xl" radius="xl" fullWidth disabled={!signature} loading={busy} onClick={confirm}>
          {t(c('confirm'))}
        </Button>
      </div>
    </>
  )
}

/**
 * What waits here: someone signing to something, its person's own signature, or photos and files to attach — under the
 * document it is for ("บิลงานขนส่ง W6910-0038", the form's own title), so whoever signs sees what they sign
 */
function Asked({ task, more, onDone }: { task: Task; more: number; onDone: () => void }) {
  const { t } = useTranslation()
  return (
    <section className={classes.sheet}>
      {(task.title || more > 0) && (
        <div className={classes.top}>
          {task.title && (
            <span className={classes.document}>
              <IconFileText size={18} stroke={1.75} aria-hidden />
              <span>{task.title}</span>
            </span>
          )}
          {more > 0 && <span className={classes.more}>{t(c('more'), { count: more })}</span>}
        </div>
      )}
      {task.kind === 'signature' && task.ask ? (
        <SignTo task={task} onDone={onDone} />
      ) : (
        <>
          <h1 className={classes.title}>{task.label}</h1>
          {task.kind === 'signature' ? (
            <SignatureWork task={task} onDone={onDone} handIn={handIn} />
          ) : (
            <FilesWork task={task} onDone={onDone} handIn={handIn} />
          )}
        </>
      )}
    </section>
  )
}

/** Nothing to do yet: the time and the day, large enough to read across the counter, and a welcome */
function Waiting() {
  const { t } = useTranslation()
  const now = useNow()
  const { toggle, fullscreen } = useFullscreenDocument()
  return (
    <section className={classes.idle}>
      <time className={classes.clock} dateTime={now.toISOString()}>
        {formatTime(now)}
      </time>
      <p className={classes.date}>{formatDate(now, { style: 'day' })}</p>
      <p className={classes.welcome}>{t(c('welcome'))}</p>
      <p className={classes.hint}>{t(c('idle'))}</p>
      {!fullscreen && (
        <Button
          variant="white"
          radius="xl"
          size="md"
          className={classes.cta}
          leftSection={<IconMaximize size={18} stroke={1.75} />}
          onClick={toggle}
        >
          {t(c('tapFullscreen'))}
        </Button>
      )}
    </section>
  )
}

/** Handed in: a check drawn in, then back to waiting */
function Thanks() {
  const { t } = useTranslation()
  return (
    <section className={classes.idle} role="status">
      <span className={classes.done} aria-hidden>
        <svg viewBox="0 0 48 48" width="56" height="56" aria-hidden>
          <path d="M13 25.5 20.5 33 35 17" pathLength={1} />
        </svg>
      </span>
      <p className={classes.welcome}>{t(c('thanks'))}</p>
    </section>
  )
}

/** Paired: waiting with the time, or showing the latest ask; thanks after each */
function Paired({ state, company, onLeave }: { state: ScreenState; company?: string; onLeave: () => void }) {
  const { t } = useTranslation()
  const client = useQueryClient()
  const [thanked, setThanked] = useState(false)

  useEffect(() => {
    if (!thanked) return
    const done = window.setTimeout(() => setThanked(false), THANKS_MS)
    return () => window.clearTimeout(done)
  }, [thanked])

  const done = () => {
    setThanked(true)
    client.invalidateQueries({ queryKey: stateKey })
  }
  const leave = () =>
    modals.openConfirmModal({
      title: t(c('leave')),
      children: <Text size="sm">{t(c('leaveConfirm'), { name: state.owner.name })}</Text>,
      labels: { confirm: t(c('leave')), cancel: t('actions.cancel') },
      confirmProps: { color: 'danger' },
      onConfirm: () => screenApi.leave().finally(onLeave),
    })

  const [task] = state.asks
  if (thanked)
    return (
      <Frame company={company} calm>
        <Thanks />
      </Frame>
    )
  if (task)
    return (
      <Frame company={company} asking>
        <Asked key={task.id} task={task} more={state.asks.length - 1} onDone={done} />
      </Frame>
    )
  return (
    <Frame
      company={company}
      calm
      foot={
        <>
          <span className={classes.owner}>
            <IconDeviceTablet size={18} stroke={1.75} aria-hidden />
            {t(c('yours'), { name: state.owner.name })}
          </span>
          {/* Unpairing sits out of the way at the bottom: a customer at the screen should not reach for it */}
          <Button variant="subtle" color="gray" size="compact-md" radius="xl" className={classes.leave} onClick={leave}>
            {t(c('leave'))}
          </Button>
        </>
      }
    >
      <Waiting />
    </Frame>
  )
}

/**
 * The portable screen (จอพกพา, screen.ts in @tnn/domain), a tablet or phone that is one person's, with no one signed in
 * (so no PIN lock shows on it). Unpaired, it pairs by the QR or code its person shows; paired, it waits with the time
 * and shows what that person's forms ask for — someone at the counter signing, their own signature, photos or files —
 * as it is asked (`/api/screen/events`), handing each in from here. Paired by HR or a lead instead, it is a branch's
 * clock screen (clock.tsx). Always light: it is paper to read and sign on
 */
export function ScreenPage() {
  const client = useQueryClient()
  useWakeLock()
  const state = useQuery({ queryKey: stateKey, queryFn: screenApi.state, retry: (count, error) => !unpairedError(error) && count < 2 })
  const clock = useQuery({
    queryKey: clockKey,
    queryFn: screenApi.clock,
    retry: (count, error) => !clockUnpaired(error) && count < 2,
    // The next code by the time this one is replaced anyway; told sooner when it is taken
    refetchInterval: (query) =>
      query.state.data && !query.state.error ? Math.max(1000, new Date(query.state.data.expiresAt).getTime() - Date.now() + 250) : false,
    refetchIntervalInBackground: true,
  })
  const site = useQuery({ queryKey: ['public-site'], queryFn: () => api.get<PublicSite>('/public/site'), staleTime: 10 * 60_000 })
  const company = site.data?.company.name || undefined
  const paired = state.isSuccess

  useEffect(() => {
    if (!paired) return
    return listenLive('/screen/events', () => client.invalidateQueries({ queryKey: stateKey }))
  }, [paired, client])

  const again = () => {
    client.resetQueries({ queryKey: stateKey })
    client.resetQueries({ queryKey: clockKey })
  }

  if (state.isPending || clock.isPending)
    return (
      <Frame company={company} calm>
        <Loader color="var(--tnn-on-primary-container)" />
      </Frame>
    )
  if (clock.data && !clockUnpaired(clock.error)) return <ClockScreen state={clock.data} company={company} />
  if (!state.data || unpairedError(state.error))
    return (
      <Frame company={company} calm>
        <Pairing onPaired={again} />
      </Frame>
    )
  return <Paired state={state.data} company={company} onLeave={again} />
}
